<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" xs="12" md="12">
        <v-alert
          v-if="!subscription.stripe_setup_intent_id && subscription.id"
          class="mt-2"
          type="info"
        >{{ $lang('companies.subscription_plan.missing') }}</v-alert>
        <v-card flat :color="highlightPayment ? 'secondary' : ''">
          <v-card-title>
            {{ $lang('companies.subscription_plan.providers.stripe.payment_method') }}
            <v-spacer></v-spacer>
            <v-btn
              v-if="!subscription.stripe_setup_intent_id && subscription.id"
              color="primary"
              @click="createSetupIntent"
              :loading="createSetupIntentRequest.loading"
            >{{ $lang('main.crud.create') }}</v-btn>
          </v-card-title>
          <v-card-text v-show="subscription.stripe_setup_intent_secret">
            <div v-show="!subscription.stripe_payment_method_id">
              <v-text-field
                v-model="billingDetails.name"
                :label="$lang('companies.subscription_plan.providers.stripe.account_holder_name')"
              />
              <v-text-field
                v-model="billingDetails.email"
                :label="$lang('companies.subscription_plan.providers.stripe.email')"
              />
              <div id="iban-element" class="mb-5"></div>
              <p>{{ $lang('companies.subscription_plan.providers.stripe.mandatory_text') }}</p>
            </div>

            <div v-if="!!subscription.stripe_payment_method_id && !!paymentMethod.sepa_debit">
              <v-text-field
                v-model="paymentMethod.sepa_debit.bank_code"
                :label="$lang('companies.subscription_plan.providers.stripe.sepa_debit.bank_code')"
                readonly
              >
                <template v-slot:append>
                  <country-flag :country="paymentMethod.sepa_debit.country" />
                </template>
              </v-text-field>
              <v-text-field
                v-model="paymentMethod.sepa_debit.last4"
                :label="$lang('companies.subscription_plan.providers.stripe.sepa_debit.last4')"
                readonly
              ></v-text-field>
            </div>
          </v-card-text>
          <v-card-actions v-if="subscription.stripe_setup_intent_secret">
            <v-spacer></v-spacer>
            <v-btn
              v-if="!subscription.stripe_payment_method_id"
              color="primary"
              @click="confirm"
              :loading="isConfirmLoading"
            >{{ $lang('companies.subscription_plan.providers.stripe.add') }}</v-btn>
            <v-btn
              v-if="!!subscription.stripe_payment_method_id"
              color="error"
              @click="detachPaymentMethod"
              :loading="detachPaymentMethodRequest.loading"
            >{{ $lang('companies.subscription_plan.providers.stripe.remove') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from "vuex";

import {
	REPLACE_SUBSCRIPTION_PLAN,
	REPLACE_SUBSCRIPTION_PLAN_RESET,
	GET_SUBSCRIPTION_PLAN_STRIPE_PAYMENT_METHOD,
	CREATE_SUBSCRIPTION_PLAN_STRIPE_SETUP_INTENT,
	CONFIRM_SUBSCRIPTION_PLAN_STRIPE_SETUP_INTENT,
	CANCEL_SUBSCRIPTION_PLAN_STRIPE_SETUP_INTENT,
	DETACH_SUBSCRIPTION_PLAN_STRIPE_PAYMENT_METHOD
} from "@/store/company-subscription/actions";

export default {
	name: "CompanySubscriptionStripe",
	props: {
		companyId: {
			required: true
    },
    company: {
      type: Object,
      required: true,
    },
    subscription: {
      type: Object,
      required: true
    },
    highlightPayment: {
      type: Boolean,
      default: false
    }
	},
	data() {
		return {
			ibanElement: null,
			isConfirmLoading: false,
			billingDetails: {},
		}
	},
	beforeMount() {
		this.billingDetails = {
			name: this.company.name,
			email: this.company.email,
			address: {
				line1: this.company.address,
				city: this.company.city,
				country: this.company.country,
			}
		}
	},
	methods: {
		createIBAN() {
			let style = {
				base: {
					color: '#32325d',
					fontSize: '16px',
					'::placeholder': {
						color: '#aab7c4'
					},
					':-webkit-autofill': {
						color: '#32325d',
					},
				},
				invalid: {
					color: '#fa755a',
					iconColor: '#fa755a',
					':-webkit-autofill': {
						color: '#fa755a',
					},
				},
			};

			let options = {
				style,
				supportedCountries: ['SEPA'],
				placeholderCountry: this.company.country,
			};

			this.$stripe.ready().then(() => {
				let existingIBAN = this.$stripe.elements.getElement('iban');
				this.ibanElement = existingIBAN || this.$stripe.elements.create('iban', options);
				this.ibanElement.mount('#iban-element');
			});
		},
		retrievePaymentMethod() {
			this.$store.dispatch(GET_SUBSCRIPTION_PLAN_STRIPE_PAYMENT_METHOD, {
				id: this.subscription.id
			});
    	},
    	detachPaymentMethod() {
			this.$store.dispatch(DETACH_SUBSCRIPTION_PLAN_STRIPE_PAYMENT_METHOD, {
				id: this.subscription.id
			}).then(() => {
        this.refreshSubscription();
				this.showNotification(this.$lang("main.crud.success_create"), "success");
			});
		},
		createSetupIntent() {
			this.$store.dispatch(CREATE_SUBSCRIPTION_PLAN_STRIPE_SETUP_INTENT, {
				id: this.subscription.id
			}).then(() => {
				this.refreshSubscription();
			});
		},
		confirm() {
			this.$stripe.ready().then(() => {
				this.isConfirmLoading = true;

				this.$stripe.confirmSepaDebitSetup(this.subscription.stripe_setup_intent_secret, {
						payment_method: {
							sepa_debit: this.ibanElement,
							billing_details: this.billingDetails,
						},
					}).then((result) => {
						if (result.error) {
							this.isConfirmLoading = false;
							this.showNotification(result.error.message, 'error');
							return;
						}

						this.$store.dispatch(REPLACE_SUBSCRIPTION_PLAN, {
							id: this.subscription.id,
							params: {
								is_active: this.$const('ConstBoolean', 'TRUE'),
								stripe_payment_method_id: result.setupIntent.payment_method
							}
						}).then(() => {
							this.isConfirmLoading = false;

							this.subscription.is_active = this.$const('ConstBoolean', 'TRUE');
							this.subscription.stripe_payment_method_id = result.setupIntent.payment_method;

							this.retrievePaymentMethod();
							this.showNotification(this.$lang("main.crud.success_create"), "success");
						});
					});
			});
		},
    refreshSubscription() {
      this.$emit('refresh');
    }
	},
	computed: {
		paymentMethod() {
			return this.getPaymentMethodRequest.data;
		},
		...mapState({
			getPaymentMethodRequest: state => state.companySubscription.getStripePaymentMethodRequest,
			detachPaymentMethodRequest: state => state.companySubscription.detachStripePaymentMethodRequest,
			createSetupIntentRequest: state => state.companySubscription.createStripeSetupIntentRequest,
			confirmSetupIntentRequest: state => state.companySubscription.confirmStripeSetupIntentRequest,
			cancelSetupIntentRequest: state => state.companySubscription.cancelStripeSetupIntentRequest,
		})
	},
	watch: {
		subscription(val) {
      if (!this.subscription.stripe_payment_method_id) {
        this.createIBAN();
      } else {
        this.retrievePaymentMethod();
      }
		}
	}
}
</script>
